import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { GoodsReceiptNoteRow } from '@/modules/inventory/goods-receipt-notes/types'

export type GrnVendorOption = {
  id: number
  vendorNameEn: string
  vendorNameAr: string
}

export type GrnPurchaseOrderOption = {
  id: number
  code: string
}

export type GrnWarehouseOption = {
  id: number
  code: string
  name: string
}

export type GrnTaxRateOption = {
  id: number
  taxRateNameEn: string
  taxRateNameAr: string
  percent: number
  isDefault: boolean
}

export type GrnOrderLinesResult = {
  order: Record<string, unknown>
  lines: Record<string, unknown>[]
}

/** Master/header from `GET /api/Grn/GetGrnById/{id}` (edit form). */
export type GrnMaster = {
  id: number
  code?: string
  reference?: string
  purchaseOrderId?: number
  date?: string
  vendorId?: number
  warehouseId?: number
  grnnumber?: string
  taxNotes?: string
  orderDate?: string
  purchaseorderCode?: string
  /** Labels for dropdowns when list APIs omit this PO/warehouse */
  warehouseName?: string
  warehouseCode?: string
}

export type GrnBatchOption = {
  id: number
  label: string
}

/** One GRN line for `itemList[n].*` + `grnLinesJson` (Swagger /api/Grn/Save). */
export type GrnSaveLine = {
  id: number
  itemId: number
  batchId: number
  quantity: number
  unitPrice: number
  discount: number
  discountAmount: number
  tax: number
  taxPercent: number
  taxableAmount: number
  taxAmount: number
  lineAmount: number
  noteId: number
  isDeleted: string
  quantityReturned: number
}

export type GrnSavePayload = {
  id: number
  code: string
  purchaseorderCode: string
  wharehouseName: string
  vendorNameEn: string
  reference: string
  purchaseOrderId: number
  date: string
  vendorId: number
  warehouseId: number
  grnnumber: string
  taxNotes: string
  attachmentPath: string
  apaccountId: number
  taxableAmount: number
  taxAmount: number
  discount: number
  totalAmount: number
  payableBalance: number
  isReturnCreated: boolean
  isBillCreated: boolean
  status: number
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  branchId: number
  attachmentFile?: File | null
  itemList: GrnSaveLine[]
}

const toArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if (Array.isArray(p.data)) return p.data
    if (Array.isArray(p.result)) return p.result
  }
  return []
}

const unwrap = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data
  }
  return payload
}

function firstNonEmptyString(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return undefined
}

/** Normalize various API envelopes for GRN line arrays */
function extractGrnLinesArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  const o = payload as Record<string, unknown>
  const keys = ['lines', 'Lines', 'data', 'items', 'grnLines', 'GrnLines', 'value', 'Value']
  for (const k of keys) {
    const v = o[k]
    if (Array.isArray(v)) return v
  }
  const inner = o.result
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner)) return inner
    const r = inner as Record<string, unknown>
    for (const k of keys) {
      const v = r[k]
      if (Array.isArray(v)) return v
    }
  }
  return toArray(payload)
}

export class GrnService {
  async getAllGrn(
    branchId: number,
    pageSize: number,
    pageNumber: number,
  ): Promise<{ items: GoodsReceiptNoteRow[]; totalCount: number }> {
    const response = await apiClient.get(endpoints.grn.getAllGrn(branchId, pageSize, pageNumber))
    const payload = response.data
    const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null
    const raw = unwrap(payload)

    let list: unknown[] = toArray(raw)
    if (list.length === 0 && raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const r = raw as Record<string, unknown>
      if (Array.isArray(r.items)) list = r.items
    }
    if (list.length === 0 && top && Array.isArray(top.items)) {
      list = top.items as unknown[]
    }

    const totalCount = Number(
      (raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>).totalRecords ?? (raw as Record<string, unknown>).totalCount
        : undefined) ??
        top?.totalRecords ??
        top?.totalCount ??
        list.length,
    )

    const items: GoodsReceiptNoteRow[] = list.map((row) => {
      const x = row as Record<string, unknown>
      const st = x?.status
      const billFlag =
        x?.isBillCreated ?? x?.IsBillCreated ?? x?.isbillcreated ?? x?.is_bill_created
      const isBillCreated =
        billFlag === true ||
        billFlag === 1 ||
        (typeof billFlag === 'string' && billFlag.toLowerCase() === 'true')
      return {
        id: Number(x?.id ?? 0),
        code: String(x?.code ?? x?.grnnumber ?? '').trim(),
        reference: String(x?.reference ?? '').trim(),
        warehouse: String(x?.wharehouseName ?? x?.warehouseName ?? x?.warehouse ?? '').trim(),
        vendor: String(x?.vendorNameEn ?? x?.vendorName ?? '').trim(),
        orderCode: String(x?.purchaseorderCode ?? x?.purchaseOrderCode ?? x?.orderCode ?? '').trim(),
        date: String(x?.date ?? '').trim(),
        total: Number(x?.totalAmount ?? x?.total ?? 0),
        status: st === null || st === undefined ? '' : String(st),
        isBillCreated,
      }
    })

    const tc = Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : items.length
    return { items, totalCount: tc }
  }

  async getAllVendors(): Promise<GrnVendorOption[]> {
    const response = await apiClient.get(endpoints.grn.getAllVendors)
    const arr = toArray(unwrap(response.data))
    return arr
      .map((raw) => {
        const x = raw as Record<string, unknown>
        return {
          id: Number(x?.vendorId ?? x?.id ?? 0),
          vendorNameEn: String(x?.vendorNameEn ?? x?.vendorName ?? ''),
          vendorNameAr: String(x?.vendorNameAr ?? ''),
        }
      })
      .filter((v) => v.id > 0)
  }

  async getOrdersByVendor(vendorId: number): Promise<GrnPurchaseOrderOption[]> {
    const response = await apiClient.get(endpoints.grn.getOrdersByVendor(vendorId))
    const arr = toArray(unwrap(response.data))
    return arr
      .map((raw) => {
        const x = raw as Record<string, unknown>
        return {
          id: Number(x?.id ?? 0),
          code: String(x?.code ?? ''),
        }
      })
      .filter((o) => o.id > 0)
  }

  async getWarehouses(branchId: number): Promise<GrnWarehouseOption[]> {
    const response = await apiClient.get(endpoints.grn.getAllWarehouses(branchId))
    const arr = toArray(unwrap(response.data))
    return arr
      .map((raw) => {
        const x = raw as Record<string, unknown>
        return {
          id: Number(x?.id ?? x?.warehouseId ?? 0),
          code: String(x?.code ?? ''),
          name: String(x?.name ?? ''),
        }
      })
      .filter((w) => w.id > 0)
  }

  async getTaxRates(): Promise<GrnTaxRateOption[]> {
    const response = await apiClient.get(endpoints.grn.getAllTaxRates)
    const arr = toArray(unwrap(response.data))
    return arr
      .map((raw) => {
        const x = raw as Record<string, unknown>
        return {
          id: Number(x?.taxRateId ?? x?.id ?? 0),
          taxRateNameEn: String(x?.taxRateNameEn ?? ''),
          taxRateNameAr: String(x?.taxRateNameAr ?? ''),
          percent: Number(x?.taxPercent ?? 0),
          isDefault: Boolean(x?.isDefault),
        }
      })
      .filter((r) => r.id > 0)
  }

  async getGrnById(id: number): Promise<GrnMaster> {
    const response = await apiClient.get(endpoints.grn.getGrnById(id))
    let payload = unwrap(response.data)
    if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'result' in payload) {
      const inner = (payload as { result: unknown }).result
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) payload = inner
    }
    const row =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload as Record<string, unknown>)
        : {}
    return {
      id: Number(row.id ?? row.Id ?? id),
      code: row.code != null ? String(row.code) : row.Code != null ? String(row.Code) : undefined,
      reference: row.reference != null ? String(row.reference) : undefined,
      purchaseOrderId:
        row.purchaseOrderId != null && row.purchaseOrderId !== ''
          ? Number(row.purchaseOrderId)
          : row.PurchaseOrderId != null && row.PurchaseOrderId !== ''
            ? Number(row.PurchaseOrderId)
            : undefined,
      date: row.date != null ? String(row.date) : undefined,
      vendorId:
        row.vendorId != null && row.vendorId !== ''
          ? Number(row.vendorId)
          : row.VendorId != null && row.VendorId !== ''
            ? Number(row.VendorId)
            : undefined,
      warehouseId:
        row.warehouseId != null && row.warehouseId !== ''
          ? Number(row.warehouseId)
          : row.WarehouseId != null && row.WarehouseId !== ''
            ? Number(row.WarehouseId)
            : undefined,
      grnnumber: row.grnnumber != null ? String(row.grnnumber) : undefined,
      taxNotes: row.taxNotes != null ? String(row.taxNotes) : undefined,
      orderDate: row.orderDate != null ? String(row.orderDate) : undefined,
      purchaseorderCode: firstNonEmptyString(row, [
        'purchaseorderCode',
        'purchaseOrderCode',
        'PurchaseOrderCode',
        'orderCode',
        'OrderCode',
        'poCode',
      ]),
      warehouseName: firstNonEmptyString(row, ['warehouseName', 'WarehouseName', 'wharehouseName']),
      warehouseCode: firstNonEmptyString(row, ['warehouseCode', 'WarehouseCode']),
    }
  }

  async getGrnLinesByGrnId(grnId: number): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(endpoints.grn.getGrnLinesByGrnId(grnId))
    let payload = unwrap(response.data)
    if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'result' in payload) {
      const inner = (payload as { result: unknown }).result
      if (Array.isArray(inner)) payload = inner
      else if (inner && typeof inner === 'object') payload = inner
    }
    const arr = extractGrnLinesArray(payload)
    return arr.map((x) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {}))
  }

  async getOrderLines(orderId: number): Promise<GrnOrderLinesResult> {
    const response = await apiClient.get(endpoints.grn.getOrderLines(orderId))
    const payload = unwrap(response.data)
    if (!payload || typeof payload !== 'object') {
      return { order: {}, lines: [] }
    }
    const p = payload as Record<string, unknown>
    const order = (p.order && typeof p.order === 'object' ? p.order : {}) as Record<string, unknown>
    const lines = toArray(p.lines)
    return {
      order,
      lines: lines.map((row) => (row && typeof row === 'object' ? (row as Record<string, unknown>) : {})),
    }
  }

  async getBatches(whId: number, itemId: number, orderId: number): Promise<GrnBatchOption[]> {
    const response = await apiClient.get(endpoints.grn.getBatches(whId, itemId, orderId))
    const arr = toArray(unwrap(response.data))
    return arr
      .map((raw) => {
        const x = raw as Record<string, unknown>
        const id = Number(x?.id ?? x?.batchId ?? 0)
        const code = String(x?.code ?? x?.batchCode ?? '').trim()
        const ref = String(x?.reference ?? '').trim()
        const label =
          [code, ref].filter(Boolean).join(' — ') || code || ref || (id > 0 ? `Batch ${id}` : '')
        return { id, label }
      })
      .filter((b) => b.id > 0)
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false')
      return
    }
    formData.append(key, String(value))
  }

  /** Property order aligned with Swagger `itemList` / `grnLinesJson` sample. */
  private normalizeLineForJson(l: GrnSaveLine): Record<string, number | string> {
    return {
      discount: l.discount,
      taxAmount: l.taxAmount,
      unitPrice: l.unitPrice,
      quantity: l.quantity,
      tax: l.tax,
      lineAmount: l.lineAmount,
      noteId: l.noteId,
      itemId: l.itemId,
      isDeleted: l.isDeleted,
      batchId: l.batchId,
      discountAmount: l.discountAmount,
      taxPercent: l.taxPercent,
      quantityReturned: l.quantityReturned,
      id: l.id,
      taxableAmount: l.taxableAmount,
    }
  }

  private buildGrnLinesJson(lines: GrnSaveLine[]): string {
    return JSON.stringify(lines.map((l) => this.normalizeLineForJson(l)))
  }

  private logOutboundPayload(action: 'save' | 'update', payload: GrnSavePayload, grnLinesJson: string) {
    const { attachmentFile, itemList, ...master } = payload
    const path = action === 'save' ? endpoints.grn.save : endpoints.grn.update
    // console.log so it always appears under default Console levels (not hidden like Verbose-only)
    console.log(
      `%c[GRN] ${action === 'save' ? 'SAVE' : 'UPDATE'}%c → POST ${path} (multipart/form-data)`,
      'font-weight:bold;color:#fff;background:#1d4ed8;padding:2px 6px;border-radius:4px',
      'color:inherit',
    )
    console.log('[GRN] Master (multipart text fields):', master)
    console.log('[GRN] itemList:', itemList)
    console.log('[GRN] grnLinesJson string:', grnLinesJson)
    console.log(
      '[GRN] AttachmentFile:',
      attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null,
    )
  }

  private buildSaveFormData(payload: GrnSavePayload, grnLinesJson: string): FormData {
    const formData = new FormData()
    const p = payload

    this.appendFormField(formData, 'Id', p.id)
    this.appendFormField(formData, 'Code', p.code)
    this.appendFormField(formData, 'purchaseorderCode', p.purchaseorderCode)
    this.appendFormField(formData, 'WharehouseName', p.wharehouseName)
    this.appendFormField(formData, 'VendorNameEn', p.vendorNameEn)
    this.appendFormField(formData, 'Reference', p.reference)
    this.appendFormField(formData, 'PurchaseOrderId', p.purchaseOrderId)
    this.appendFormField(formData, 'Date', p.date)
    this.appendFormField(formData, 'VendorId', p.vendorId)
    this.appendFormField(formData, 'WarehouseId', p.warehouseId)
    this.appendFormField(formData, 'Grnnumber', p.grnnumber)
    this.appendFormField(formData, 'TaxNotes', p.taxNotes)
    this.appendFormField(formData, 'AttachmentPath', p.attachmentPath)
    this.appendFormField(formData, 'ApaccountId', p.apaccountId)
    this.appendFormField(formData, 'TaxableAmount', p.taxableAmount)
    this.appendFormField(formData, 'TaxAmount', p.taxAmount)
    this.appendFormField(formData, 'Discount', p.discount)
    this.appendFormField(formData, 'TotalAmount', p.totalAmount)
    this.appendFormField(formData, 'PayableBalance', p.payableBalance)
    this.appendFormField(formData, 'IsReturnCreated', p.isReturnCreated)
    this.appendFormField(formData, 'IsBillCreated', p.isBillCreated)
    this.appendFormField(formData, 'Status', p.status)
    this.appendFormField(formData, 'CreatedDate', p.createdDate)
    this.appendFormField(formData, 'CreatedBy', p.createdBy)
    this.appendFormField(formData, 'ModifiedDate', p.modifiedDate)
    this.appendFormField(formData, 'ModifiedBy', p.modifiedBy)
    this.appendFormField(formData, 'BranchId', p.branchId)

    p.itemList.forEach((line, index) => {
      this.appendFormField(formData, `itemList[${index}].discount`, line.discount)
      this.appendFormField(formData, `itemList[${index}].taxAmount`, line.taxAmount)
      this.appendFormField(formData, `itemList[${index}].unitPrice`, line.unitPrice)
      this.appendFormField(formData, `itemList[${index}].quantity`, line.quantity)
      this.appendFormField(formData, `itemList[${index}].tax`, line.tax)
      this.appendFormField(formData, `itemList[${index}].lineAmount`, line.lineAmount)
      this.appendFormField(formData, `itemList[${index}].noteId`, line.noteId)
      this.appendFormField(formData, `itemList[${index}].itemId`, line.itemId)
      this.appendFormField(formData, `itemList[${index}].isDeleted`, line.isDeleted)
      this.appendFormField(formData, `itemList[${index}].batchId`, line.batchId)
      this.appendFormField(formData, `itemList[${index}].discountAmount`, line.discountAmount)
      this.appendFormField(formData, `itemList[${index}].taxPercent`, line.taxPercent)
      this.appendFormField(formData, `itemList[${index}].quantityReturned`, line.quantityReturned)
      this.appendFormField(formData, `itemList[${index}].id`, line.id)
      this.appendFormField(formData, `itemList[${index}].taxableAmount`, line.taxableAmount)
    })

    formData.append('grnLinesJson', grnLinesJson)

    if (p.attachmentFile) {
      formData.append('AttachmentFile', p.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    return formData
  }

  /** Swagger success: `{ result: { isError: false, msg, id } }` — treat `isError: true` as failure. */
  private throwIfGrnResultError(data: unknown) {
    if (!data || typeof data !== 'object') return
    const root = data as Record<string, unknown>
    const r = root.result
    if (!r || typeof r !== 'object') return
    const o = r as Record<string, unknown>
    if (o.isError !== true) return
    const msg = typeof o.msg === 'string' && o.msg.trim() ? o.msg.trim() : 'Operation failed.'
    const err = new Error(msg) as Error & { response?: { status: number; data: unknown } }
    err.response = { status: 400, data }
    throw err
  }

  async save(payload: GrnSavePayload): Promise<void> {
    const grnLinesJson = this.buildGrnLinesJson(payload.itemList)
    this.logOutboundPayload('save', payload, grnLinesJson)
    const formData = this.buildSaveFormData(payload, grnLinesJson)
    const res = await apiClient.post(endpoints.grn.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    this.throwIfGrnResultError(res.data)
  }

  /** Same body as `save` (multipart/form-data); use for existing GRN records. */
  async update(payload: GrnSavePayload): Promise<void> {
    const grnLinesJson = this.buildGrnLinesJson(payload.itemList)
    this.logOutboundPayload('update', payload, grnLinesJson)
    const formData = this.buildSaveFormData(payload, grnLinesJson)
    const res = await apiClient.post(endpoints.grn.update, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    this.throwIfGrnResultError(res.data)
  }

  async delete(id: number): Promise<void> {
    await apiClient.post(endpoints.grn.delete(id))
  }

  /** POST body: JSON array of GRN ids (Swagger: `application/json-patch+json`). */
  async combinedBill(grnIds: number[]): Promise<void> {
    const ids = grnIds.filter((id) => Number.isFinite(id) && id > 0)
    await apiClient.post(endpoints.grn.combinedBill, ids, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })
  }

  async convertToBill(id: number): Promise<void> {
    await apiClient.post(endpoints.grn.convertToBill(id))
  }
}

export const grnService = new GrnService()
